import React, { useEffect, useMemo, useState } from 'react';
import { Accordion, Box, Divider } from '@chakra-ui/react';

import { AppsV2InputType, MessageStageType } from '../../../../api/types';
import { AppToggle } from '../../../../clients/unleash/app-toggle-names';
import { WithFeatureFlag } from '../../../WithFeatureFlag/WithFeatureFlag';
import { MessageLoading } from '../Loading/Loading';
import { Timer } from '../Timer/Timer';
import { CommonChatMessageProps } from '../types';

import { AssistantInput } from './Input/AssistantInput';
import { countResults } from './ToolCallResults/helpers';
import { ToolCalls } from './ToolCalls';
import { ToolResults } from './ToolResults';

import styles from './EnhancedMessage.module.scss';

interface EnhancedMessageProps extends Pick<CommonChatMessageProps, 'message' | 'conversation'> {
    showContent: boolean;
    showLoading?: boolean;
    showTimer?: boolean;
}

const SUPPORTED_INPUT_TYPES = [AppsV2InputType.FULL_PAGE, AppsV2InputType.SELECTED_TEXT];

export const EnhancedMessage: React.FC<EnhancedMessageProps> = ({
    message,
    conversation,
    showContent,
    showLoading,
    showTimer,
}) => {
    const [open, setOpen] = useState<number[]>([]);
    const { input, tool_calls, tool_calls_results, stage, message_id, created_at, finished_at } = message;

    // We want to show the input if the message relates to an app
    // or if the message is the first message in the conversation of a full chat app
    const appSnapshot = message.app_snapshot ?? conversation.app_snapshot;
    const isAppInputTypeOk = appSnapshot && SUPPORTED_INPUT_TYPES.includes(appSnapshot.input_type);
    const isInlineAppMessage = !!message.app_id;
    const isFullChatAppConversation = !!conversation.app_id;

    const isFirstFullChatAppMessage = isFullChatAppConversation && conversation.messages.length < 2;
    const shouldShowInput = !!input && isAppInputTypeOk && (isInlineAppMessage || isFirstFullChatAppMessage);
    const index = shouldShowInput ? 1 : 0;
    const toolCallsCount = tool_calls?.length ?? 0;
    const toolCallsResultsCount = useMemo(() => {
        if (!tool_calls_results || tool_calls_results?.length === 0) {
            return 0;
        }
        return countResults(tool_calls_results);
    }, [tool_calls_results]);

    useEffect(() => {
        if (stage === MessageStageType.GATHERING_INPUT && shouldShowInput) {
            setOpen([0]);
        }
        if (stage === MessageStageType.TOOL_CALLS) {
            setOpen([index]);
        }
        if (stage === MessageStageType.ANSWER) {
            setOpen([]);
        }
    }, [stage, index, shouldShowInput, toolCallsResultsCount]);

    if (stage === MessageStageType.LOADING || (!shouldShowInput && stage === MessageStageType.GATHERING_INPUT)) {
        // If the stage is loading and the showLoading prop is true, we show a loading state
        // If the stage is gathering input but the input should not be shown, we show a loading state or null
        if (!showLoading) {
            return null;
        }

        return (
            <Box className={styles.loading}>
                <MessageLoading />
                {showTimer && <Timer createdAt={created_at} finishedAt={finished_at} />}
            </Box>
        );
    }

    if (!toolCallsCount && !shouldShowInput) {
        // If there are no tool calls and the input should not be shown, we show null
        return null;
    }

    return (
        <Box>
            <Accordion allowMultiple index={open} onChange={(index: number[]) => setOpen(index)}>
                {shouldShowInput && <AssistantInput input={input} />}
                <ToolCalls toolCalls={tool_calls} />
                <WithFeatureFlag featureFlag={AppToggle.CHAT_TAB_TOOL_CALL_RESULTS}>
                    <ToolResults
                        count={toolCallsResultsCount}
                        conversationId={conversation.conversation_id}
                        messageId={message_id}
                    />
                </WithFeatureFlag>
                {showContent && <Divider margin="10px" height="1px" />}
            </Accordion>
            {showTimer && <Timer createdAt={created_at} finishedAt={finished_at} />}
        </Box>
    );
};
